// src/services/api.js
import axios from "axios";

////////////////////////////////////////////////////////////////////////
// BASE URL
////////////////////////////////////////////////////////////////////////
// The BASE_URL is set via environment variable (or defaults to production URL)
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://forms.ttanalytics.net";

////////////////////////////////////////////////////////////////////////
// 1) ONBOARDING (EXISTING) - POST /api/onboarding/existing-with-file
// Duplicate prevention and custom success message are now handled by the backend.
////////////////////////////////////////////////////////////////////////
export async function submitOnboardingExisting(formData) {
  console.debug("[submitOnboardingExisting] Called with formData:", formData);

  const data = new FormData();

  // Required Fields
  data.append("first_name", formData.first_name);
  data.append("last_name", formData.last_name);
  data.append("email1", formData.email1);
  data.append("date_of_birth", formData.date_of_birth);
  data.append("address_line_1", formData.address_line_1);
  data.append("address_line_2", formData.address_line_2);
  data.append("address_city", formData.address_city);
  data.append("postcode", formData.postcode);
  data.append("county", formData.county);
  data.append("phone_number", formData.phone_number);
  data.append("occupation", formData.occupation);
  data.append("working_hours", formData.working_hours);
  data.append("payment_full_name", formData.payment_full_name);
  data.append("payment_sort_code", formData.payment_sort_code);
  data.append("payment_account_number", formData.payment_account_number);
  // Append referral1 only if non‑empty
  if (formData.referral1 && formData.referral1.trim() !== "") {
    data.append("referral1", formData.referral1);
  }
  
  data.append("user_code", formData.user_code);

  // Booleans
  data.append("high_demand_events", formData.high_demand_events);
  data.append("buying_more_tickets", formData.buying_more_tickets);
  data.append("transfer_service", formData.transfer_service);
  data.append("allow_tt_signups", formData.allow_tt_signups);
  data.append("all_tt_manage_passwords", formData.all_tt_manage_passwords);
  data.append("allow_tt_create_emails", formData.allow_tt_create_emails);
  data.append("live_rebate_updates", formData.live_rebate_updates);

  // Terms acceptance (required)
  data.append("terms_accepted", formData.terms_accepted);

  // Optional File
  if (formData.file) {
    console.debug(
      "[submitOnboardingExisting] Attaching file:",
      formData.file.name
    );
    data.append("file", formData.file);
  }

  // Optional Signature
  if (formData.signature_base64) {
    console.debug(
      "[submitOnboardingExisting] Attaching signatureBase64 data."
    );
    data.append("signature_base64", formData.signature_base64);
  }

  try {
    console.debug(
      "[submitOnboardingExisting] Sending POST request to:",
      `${BASE_URL}/api/onboarding/existing-with-file`
    );
    const response = await axios.post(
      `${BASE_URL}/api/onboarding/existing-with-file`,
      data,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    console.debug("[submitOnboardingExisting] Response data:", response.data);
    return response.data; // expected response: { id, message }
  } catch (error) {
    console.error("[submitOnboardingExisting] Error:", error);
    throw error;
  }
}

////////////////////////////////////////////////////////////////////////
// 2) ONBOARDING (NEW) - POST /api/onboarding/new-with-file
////////////////////////////////////////////////////////////////////////
export async function submitOnboardingNew(formData) {
  console.debug("[submitOnboardingNew] Called with formData:", formData);

  const data = new FormData();

  // Required Fields
  data.append("first_name", formData.first_name);
  data.append("last_name", formData.last_name);
  data.append("email1", formData.email1);
  data.append("phone_number", formData.phone_number);
  data.append("date_of_birth", formData.date_of_birth);
  data.append("address_line_1", formData.address_line_1);
  data.append("address_line_2", formData.address_line_2);
  data.append("address_city", formData.address_city);
  data.append("postcode", formData.postcode);
  data.append("county", formData.county);
  data.append("occupation", formData.occupation);
  data.append("working_hours", formData.working_hours);
  data.append("payment_full_name", formData.payment_full_name);
  data.append("payment_sort_code", formData.payment_sort_code);
  data.append("payment_account_number", formData.payment_account_number);
  // Append referral1 only if non‑empty
  if (formData.referral1 && formData.referral1.trim() !== "") {
    data.append("referral1", formData.referral1);
  }

  // Booleans
  data.append("high_demand_events", formData.high_demand_events);
  data.append("buying_more_tickets", formData.buying_more_tickets);
  data.append("transfer_service", formData.transfer_service);
  data.append("allow_tt_signups", formData.allow_tt_signups);
  data.append("all_tt_manage_passwords", formData.all_tt_manage_passwords);
  data.append("allow_tt_create_emails", formData.allow_tt_create_emails);
  data.append("live_rebate_updates", formData.live_rebate_updates);

  // Terms acceptance (required)
  data.append("terms_accepted", formData.terms_accepted);

  // Optional File
  if (formData.file) {
    console.debug("[submitOnboardingNew] Attaching file:", formData.file.name);
    data.append("file", formData.file);
  }

  // Optional Signature
  if (formData.signature_base64) {
    console.debug("[submitOnboardingNew] Attaching signatureBase64 data.");
    data.append("signature_base64", formData.signature_base64);
  }

  try {
    console.debug(
      "[submitOnboardingNew] Sending POST request to:",
      `${BASE_URL}/api/onboarding/new-with-file`
    );
    const response = await axios.post(
      `${BASE_URL}/api/onboarding/new-with-file`,
      data,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    console.debug("[submitOnboardingNew] Response data:", response.data);
    return response.data; // expected response: { id, message }
  } catch (error) {
    console.error("[submitOnboardingNew] Error:", error);
    throw error;
  }
}

////////////////////////////////////////////////////////////////////////
// 3) SUB‑ACCOUNT CONSENT - POST /api/partner/account/consent-with-file
////////////////////////////////////////////////////////////////////////
export async function submitSubAccountConsent(formData) {
  console.debug("[submitSubAccountConsent] Called with formData:", formData);

  const data = new FormData();

  // Convert ID to string to ensure correct form‑data
  data.append("registration_id", formData.registration_id.toString());

  // Required fields
  data.append("partner_first_name", formData.partner_first_name);
  data.append("partner_last_name", formData.partner_last_name);
  data.append("main_first_name", formData.main_first_name);
  data.append("main_last_name", formData.main_last_name);
  data.append("email", formData.email);

  // Additional required fields
  data.append("main_user_code", formData.main_user_code);
  data.append("phone_number", formData.phone_number);
  data.append("address_line_1", formData.address_line_1);
  data.append("address_line_2", formData.address_line_2);
  data.append("address_city", formData.address_city);
  data.append("postcode", formData.postcode);
  data.append("county", formData.county);

  // Booleans
  data.append("consent_for_purchases", formData.consent_for_purchases);
  data.append("confirm_correct_information", formData.confirm_correct_information);
  data.append("data_sharing_privacy_consent", formData.data_sharing_privacy_consent);
  data.append("rules_consent", formData.rules_consent);
  data.append("agree_terms", formData.agree_terms);

  // Terms acceptance (mapped to agree_terms)
  data.append("agree_terms", formData.agree_terms);

  // Optional File
  if (formData.file) {
    console.debug("[submitSubAccountConsent] Attaching file:", formData.file.name);
    data.append("file", formData.file);
  }

  // Optional Signature
  if (formData.signature_base64) {
    console.debug(
      "[submitSubAccountConsent] Attaching signatureBase64 data."
    );
    data.append("signature_base64", formData.signature_base64);
  }

  try {
    console.debug(
      "[submitSubAccountConsent] Sending POST request to:",
      `${BASE_URL}/api/partner/account/consent-with-file`
    );
    const response = await axios.post(
      `${BASE_URL}/api/partner/account/consent-with-file`,
      data,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    console.debug("[submitSubAccountConsent] Response data:", response.data);
    return response.data;
  } catch (error) {
    console.error("[submitSubAccountConsent] Error:", error);
    throw error;
  }
}

////////////////////////////////////////////////////////////////////////
// 4) SUB‑ACCOUNT REQUEST (PARTNER ACCOUNT REGISTRATION)
//     POST /api/partner/account/registration
////////////////////////////////////////////////////////////////////////
export async function submitSubAccountRequest(formData) {
  console.debug("[submitSubAccountRequest] Called with formData:", formData);

  // JSON payload
  const payload = {
    main_first_name: formData.main_first_name,
    main_last_name: formData.main_last_name,
    partner_first_name: formData.partner_first_name,
    partner_last_name: formData.partner_last_name,
    main_user_code: formData.main_user_code,
    main_email: formData.main_email,
    partner_phone_number: formData.partner_phone_number,
    address_line_1: formData.address_line_1,
    address_line_2: formData.address_line_2,
    address_city: formData.address_city,
    postcode: formData.postcode,
    county: formData.county,
    consent_requirement: formData.consent_requirement,
    allow_email_creation: formData.allow_email_creation,
    partner_account_consent: formData.partner_account_consent,
    partner_email: formData.partner_email,
    terms_accepted: formData.terms_accepted,
    consent_link: formData.consent_link,
  };

  try {
    console.debug(
      "[submitSubAccountRequest] Sending POST request to:",
      `${BASE_URL}/api/partner/account/registration`
    );
    const response = await axios.post(
      `${BASE_URL}/api/partner/account/registration`,
      payload,
      { headers: { "Content-Type": "application/json" } }
    );
    console.debug("[submitSubAccountRequest] Response data:", response.data);
    return response.data;
  } catch (error) {
    console.error("[submitSubAccountRequest] Error:", error);
    throw error;
  }
}

////////////////////////////////////////////////////////////////////////
// 5) ADMIN: CREATE PLATFORM USER - POST /api/admin/platform-user
////////////////////////////////////////////////////////////////////////
export async function createPlatformUser(userData, token) {
  console.debug(
    "[createPlatformUser] Sending POST request to:",
    `${BASE_URL}/api/admin/platform-user`
  );
  const response = await axios.post(
    `${BASE_URL}/api/admin/platform-user`,
    userData,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  console.debug("[createPlatformUser] Response data:", response.data);
  return response.data;
}

////////////////////////////////////////////////////////////////////////
// 6) ADMIN LOGIN - POST /api/admin/login
////////////////////////////////////////////////////////////////////////
export async function adminLogin(email, password) {
  console.debug("[adminLogin] Called with email:", email);
  try {
    const formData = new FormData();
    formData.append("username", email);
    formData.append("password", password);

    console.debug(
      "[adminLogin] Sending POST request to:",
      `${BASE_URL}/api/admin/login`
    );
    const response = await axios.post(
      `${BASE_URL}/api/admin/login`,
      formData,
      {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      }
    );
    console.debug("[adminLogin] Response data:", response.data);
    return response.data;
  } catch (error) {
    console.error("[adminLogin] Error:", error);
    throw error;
  }
}

////////////////////////////////////////////////////////////////////////
// 7) ADMIN - FETCH ALL FORM RECORDS
////////////////////////////////////////////////////////////////////////

// GET /api/admin/onboarding/existing/all
export async function fetchAllOnboardingExisting(token) {
  console.debug("[fetchAllOnboardingExisting] Called with token:", token);
  try {
    const response = await axios.get(
      `${BASE_URL}/api/admin/onboarding/existing/all`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error("[fetchAllOnboardingExisting] Error:", error);
    throw error;
  }
}

// GET /api/admin/onboarding/new/all
export async function fetchAllOnboardingNew(token) {
  console.debug("[fetchAllOnboardingNew] Called with token:", token);
  try {
    const response = await axios.get(
      `${BASE_URL}/api/admin/onboarding/new/all`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error("[fetchAllOnboardingNew] Error:", error);
    throw error;
  }
}

// GET /api/admin/partner/account/registration/all
export async function fetchAllSubAccountRequests(token) {
  console.debug("[fetchAllSubAccountRequests] Called with token:", token);
  try {
    const response = await axios.get(
      `${BASE_URL}/api/admin/partner/account/registration/all`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error("[fetchAllSubAccountRequests] Error:", error);
    throw error;
  }
}

// GET /api/admin/partner/account/consent/all
export async function fetchAllSubAccountConsents(token) {
  console.debug("[fetchAllSubAccountConsents] Called with token:", token);
  try {
    const response = await axios.get(
      `${BASE_URL}/api/admin/partner/account/consent/all`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error("[fetchAllSubAccountConsents] Error:", error);
    throw error;
  }
}

////////////////////////////////////////////////////////////////////////
// 8) ADMIN - UPDATE STATUS ENDPOINTS (PATCH)
////////////////////////////////////////////////////////////////////////

// PATCH /api/admin/onboarding/existing/:userId/status
export async function updateOnboardingExistingStatus(
  userId,
  newStatus,
  token
) {
  try {
    await axios.patch(
      `${BASE_URL}/api/admin/onboarding/existing/${userId}/status`,
      { status: newStatus },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
  } catch (error) {
    console.error("[updateOnboardingExistingStatus] Error:", error);
    throw error;
  }
}

// PATCH /api/admin/onboarding/new/:userId/status
export async function updateOnboardingNewStatus(userId, newStatus, token) {
  try {
    await axios.patch(
      `${BASE_URL}/api/admin/onboarding/new/${userId}/status`,
      { status: newStatus },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
  } catch (error) {
    console.error("[updateOnboardingNewStatus] Error:", error);
    throw error;
  }
}

// PATCH /api/admin/partner/account/registration/:registrationId/status
export async function updateSubAccountRequestStatus(
  registrationId,
  newStatus,
  token
) {
  try {
    await axios.patch(
      `${BASE_URL}/api/admin/partner/account/registration/${registrationId}/status`,
      { status: newStatus },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
  } catch (error) {
    console.error("[updateSubAccountRequestStatus] Error:", error);
    throw error;
  }
}

// PATCH /api/admin/partner/account/consent/:consentId/status
export async function updateSubAccountConsentStatus(
  consentId,
  newStatus,
  token
) {
  try {
    await axios.patch(
      `${BASE_URL}/api/admin/partner/account/consent/${consentId}/status`,
      { status: newStatus },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
  } catch (error) {
    console.error("[updateSubAccountConsentStatus] Error:", error);
    throw error;
  }
}

////////////////////////////////////////////////////////////////////////
// 8.1) ADMIN – PATCH WHOLE RECORD (generic "Edit" modal helpers)
////////////////////////////////////////////////////////////////////////

// PATCH the entire record (Onboarding Existing)
export async function patchOnboardingExisting(userId, patch, token) {
  await axios.patch(
    `${BASE_URL}/api/admin/onboarding/existing/${userId}`,
    patch,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
}

// PATCH the entire record (Onboarding New)
export async function patchOnboardingNew(userId, patch, token) {
  await axios.patch(
    `${BASE_URL}/api/admin/onboarding/new/${userId}`,
    patch,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
}

////////////////////////////////////////////////////////////////////////
// 9) ADMIN - PATCH OnboardingNew to Save CRM Password
//     /api/admin/onboarding/new/:userId/password
////////////////////////////////////////////////////////////////////////
export async function patchOnboardingNewPassword(userId, crmPassword, token) {
  try {
    await axios.patch(
      `${BASE_URL}/api/admin/onboarding/new/${userId}/password`,
      { crm_password: crmPassword },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
  } catch (error) {
    console.error("[patchOnboardingNewPassword] Error:", error);
    throw error;
  }
}

////////////////////////////////////////////////////////////////////////
// TT Analytics API Endpoints
////////////////////////////////////////////////////////////////////////

// Get financial transactions
export async function getFinancialTransactions(token) {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/admin/analytics/transactions`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.data;
  } catch (error) {
    console.error('[getFinancialTransactions] Error:', error);
    throw error;
  }
}

// Get rebates with date range
export async function getRebates(token, dateFrom, dateTo) {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/admin/analytics/rebates`,
      {
        params: {
          date_from: dateFrom,
          date_to: dateTo
        },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.data;
  } catch (error) {
    console.error('[getRebates] Error:', error);
    throw error;
  }
}

// Get notification history
export async function getNotificationHistory(token, limit = 26) {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/admin/analytics/notifications`,
      {
        params: { limit },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.data;
  } catch (error) {
    console.error('[getNotificationHistory] Error:', error);
    throw error;
  }
}

// Get monthly leaderboard
export async function getMonthlyLeaderboard(token) {
  try {
    const response = await axios.get(
      `${BASE_URL}/api/admin/analytics/leaderboard`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data.data;
  } catch (error) {
    console.error('[getMonthlyLeaderboard] Error:', error);
    throw error;
  }
}
